import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import NewAppShell from '../NewAppShell';
import Header from '../Header';
import Fecho from '../home/Fecho';
import useRevelar from '../home/useRevelar';
import { noIdioma } from '../../config/posts';
import { EMAIL_DE_CONTATO } from '../../config/contato';
import { lerPrefixo } from '../../config/seo';
import { Link, useIdioma } from '../../i18n';
import { useFormato } from '../../i18n/formato';
import '../../styles/NewHome.css';
import '../../styles/Juridico.css';

/**
 * Moldura da Política de Privacidade e dos Termos, no desenho das páginas
 * novas. Substitui o `LegalLayout` sem tocar nele: quem escolhe é o import em
 * `App.js`.
 *
 * O documento chega pronto em `doc` (src/juridico/*.js), com cada texto em pt,
 * en e es. Vale o inglês: fora dele, a nota de tradução diz isso e oferece o
 * original. As seções vêm de um array só, então os `id` das âncoras são os
 * mesmos nos três idiomas e um link para `#cookies` serve a qualquer leitor.
 *
 * Mesmo arranjo do LegalLayout para quem está logado: o menu lateral do app
 * aponta para cá, e a página abre dentro do NewAppShell.
 */

const DATA_LONGA = { day: 'numeric', month: 'long', year: 'numeric' };

const MolduraJuridica = ({ user, doc, irmao }) => {
  const { t, idioma } = useIdioma();
  const { data } = useFormato();
  const { hash, pathname } = useLocation();
  const raiz = useRef(null);
  const indiceRef = useRef(null);
  const [ativa, setAtiva] = useState(doc.secoes[0].id);
  const [copiado, setCopiado] = useState('');
  useRevelar(raiz);

  const no = (campo) => noIdioma(campo, idioma);
  const titulo = no(doc.titulo);

  /* Os links entre os documentos chegam como `/privacy-policy#retencao`; o
     router troca de página mas não rola até a âncora (o mesmo do NewHome). */
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  /* Seção ativa: a última cujo título já passou do terço superior da tela. */
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const passadas = new Set();
    const observador = new IntersectionObserver(
      (itens) => {
        for (const item of itens) {
          if (item.isIntersecting || item.boundingClientRect.top < 0) passadas.add(item.target.id);
          else passadas.delete(item.target.id);
        }
        setAtiva(doc.secoes.filter((s) => passadas.has(s.id)).pop()?.id || doc.secoes[0].id);
      },
      { rootMargin: '0px 0px -66% 0px' }
    );
    doc.secoes.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observador.observe(el);
    });
    return () => observador.disconnect();
  }, [doc]);

  /* No telefone o índice começa fechado, para o texto vir logo depois da cabeça. */
  useEffect(() => {
    if (indiceRef.current && window.matchMedia('(max-width: 1100px)').matches) indiceRef.current.open = false;
  }, []);

  const fecharNoTelefone = () => {
    if (window.matchMedia('(max-width: 1100px)').matches) indiceRef.current.open = false;
  };

  /* Copia o endereço da seção em leitura: é o que se manda a alguém para
     apontar uma cláusula. Mesmo comportamento do "Copiar link" do post. */
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#${ativa}`);
      setCopiado(t('notas.linkCopiado'));
    } catch {
      setCopiado(t('notas.naoCopiou'));
    }
    setTimeout(() => setCopiado(''), 2400);
  };

  const conteudo = (
    <main className="nh juridico" ref={raiz}>
      <header className="nh-grade juridico-cabeca">
        <div className="juridico-campo">
          <div className="juridico-abre">
            <h1>{titulo}</h1>
            <p className="juridico-lead">{no(doc.lead)}</p>
            <div className="juridico-meta">
              <p>
                {no(doc.atualizado)}{' '}
                <time dateTime={doc.atualizadoEm}>{data(`${doc.atualizadoEm}T12:00:00`, DATA_LONGA)}</time>.{' '}
                {no(doc.provisoria)}{' '}
                <a href={`mailto:${EMAIL_DE_CONTATO}`}>{EMAIL_DE_CONTATO}</a>.
              </p>
              {idioma !== 'en' && (
                <p>
                  {t('juridico.traducao')}{' '}
                  <Link to={lerPrefixo(pathname).caminho} idioma="en" lang="en" hrefLang="en">
                    {t('juridico.lerEmIngles')}
                  </Link>
                </p>
              )}
            </div>
          </div>

          <aside className="juridico-resumo" aria-labelledby="juridico-resumo-titulo" data-r style={{ '--i': 1 }}>
            <h2 id="juridico-resumo-titulo">{t('juridico.emResumo')}</h2>
            <ul>
              {doc.resumo.map((r) => (
                <li key={r.ancora + no(r.texto)}>
                  <a href={`#${r.ancora}`}>
                    {no(r.texto)} <span className="nh-seta" aria-hidden="true">→</span>
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </header>

      <div className="nh-grade juridico-corpo">
        <details className="juridico-indice" open ref={indiceRef}>
          <summary>{t('juridico.nestaPagina')}</summary>
          <ol>
            {doc.secoes.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} aria-current={ativa === s.id ? 'true' : undefined} onClick={fecharNoTelefone}>
                  {no(s.titulo)}
                </a>
              </li>
            ))}
          </ol>
        </details>

        <article className="juridico-texto">
          {doc.secoes.map((s) => (
            <section key={s.id} id={s.id} aria-labelledby={`${s.id}-titulo`}>
              <h2 id={`${s.id}-titulo`}>
                <a href={`#${s.id}`}>{no(s.titulo)}</a>
              </h2>
              {no(s.corpo)}
            </section>
          ))}
          <p className="juridico-irmao">
            {t('juridico.leiaTambem')} <Link to={irmao.caminho}>{t(irmao.nome)}</Link>
          </p>
        </article>

        <aside className="juridico-trilho" aria-label={t('notas.compartilhar')}>
          <button type="button" className="juridico-copiar" onClick={copiar}>
            {t('juridico.copiarSecao')}
          </button>
          <span className="juridico-copiado" role="status">
            {copiado}
          </span>
        </aside>
      </div>
    </main>
  );

  if (user) {
    return (
      <NewAppShell user={user} crumbs={[{ rotulo: t('menu.legal') }, { rotulo: titulo }]}>
        {conteudo}
      </NewAppShell>
    );
  }

  return (
    <>
      <Header />
      {conteudo}
      <Fecho />
    </>
  );
};

export default MolduraJuridica;
